import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Box, Spacer } from '@chakra-ui/react';
import { prop, uniqBy } from 'ramda';

import { SensitiveOperationType } from '__generated__/GQL';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import SMSVerificationModal from 'components/SMSVerificationModal';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import PageHeader from 'features/Payments/Pages/Transfers/Pages/BulkTransfers/components/PageHeader';
import * as S from 'features/Payments/Pages/Transfers/Pages/BulkTransfers/styles';
import * as Layout from 'layouts/StepperLayout';

import { useBulkTransferContext } from '../../context';
import TopCard from './components/TopCard';
import TransfersList from './components/TransfersList';
import { CreateBulkTransferDocument } from './hooks/createBulkTransfer.gql';
import locales from './locales';
import { formatTransfer } from './utils';

const BulkTransfersList: FC<StepProps> = ({ goBack, goNext }) => {
  const { setSharedState, sharedState } = useBulkTransferContext();
  const { showToast } = useToast();

  const [createBulkTransferMutation, { data, loading }] = useMutation(
    CreateBulkTransferDocument,
  );

  const [isSmsVerificationModalVisible, setIsSmsVerificationModalVisible] =
    useState(false);

  const recipientsWithoutId = sharedState.transfers
    .map(({ bankTransferRecipient }) => bankTransferRecipient)
    .filter(({ bankTransferRecipientId }) => bankTransferRecipientId === null);

  const newRecipientsCount = uniqBy(prop('iban'), recipientsWithoutId).length;
  const newRecipientMessage = locales.formatString(
    newRecipientsCount > 1
      ? locales.newRecipientsInfo.many
      : locales.newRecipientsInfo.one,
    newRecipientsCount,
  ) as string;

  const onConfirmationSuccess = async (shortLivedToken: string) => {
    try {
      const { data: res } = await createBulkTransferMutation({
        context: {
          headers: {
            'short-lived-token': shortLivedToken,
          },
        },
        variables: {
          input: {
            bankAccountId: sharedState.bankAccountId,
            bucketName: sharedState.bucketName,
            companyUserId: sharedState.companyUserId,
            fileRef: sharedState.fileRef,
            transfers: sharedState.transfers.map(formatTransfer),
          },
        },
      });
      setSharedState({
        ...sharedState,
        totalAmount:
          res?.createBulkBankTransfer.totalAmount ?? sharedState.totalAmount,
      });
      goNext();
    } catch {
      showToast({
        message: locales.createBulkTransferFailed,
        type: 'error',
      });
    }
  };

  return (
    <Layout.Root>
      <Layout.Body>
        <S.NavigationHeaderWrapper>
          <BackButton />
        </S.NavigationHeaderWrapper>
        <Layout.BodyScrollableSection>
          <PageHeader title={locales.title} />
          <Box marginBottom="space-40">
            <TopCard
              remainingBalance={
                sharedState.nextBalance - sharedState.totalAmount
              }
              totalAmount={sharedState.totalAmount}
            />
          </Box>
          <Box marginBottom="space-16">
            <Text fontWeight="weight-500" variant="primary">
              {locales.formatString(
                locales.listLabel,
                sharedState.transfers.length,
              )}
            </Text>
          </Box>
          <TransfersList transfers={sharedState.transfers} />
        </Layout.BodyScrollableSection>
        <Layout.FooterContainer>
          <StepperProgressBar />
          <Layout.FooterButtonsContainer>
            <Button onClick={() => goBack()} variant="inline-primary">
              {locales.cancel}
            </Button>
            <Spacer />
            <Button
              icon="arrow-right"
              isDisabled={data !== undefined}
              isLoading={loading}
              onClick={() => setIsSmsVerificationModalVisible(true)}
            >
              {locales.sendAllTransfers}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
      <Layout.SidebarWithContent>
        {newRecipientsCount > 0 ? (
          <Callout>
            <Callout.Content>{newRecipientMessage}</Callout.Content>
          </Callout>
        ) : null}
      </Layout.SidebarWithContent>

      <SMSVerificationModal
        actionData={{}}
        actionType={SensitiveOperationType.CreateBulkTransferBeneficiaries}
        isOpen={isSmsVerificationModalVisible}
        onClose={() => setIsSmsVerificationModalVisible(false)}
        onSuccess={onConfirmationSuccess}
      />
    </Layout.Root>
  );
};

export default BulkTransfersList;
